import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, printPdf } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/common/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination, usePagination } from "@/components/common/Pagination";
import { toast } from "sonner";
import { Printer, Plus, MagnifyingGlass, Receipt } from "@phosphor-icons/react";

export default function OPDPage() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  useEffect(() => { api.get("/opd").then((r) => setList(r.data || [])); }, []);

  // Client-side filter mirrors the Patients page behaviour: matches MRN,
  // patient name, OPD ID, receipt number, doctor or diagnosis.
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter((v) => {
      const hay = [
        v.patient_name, v.mrn, v.opd_id, v.receipt_no, v.doctor, v.diagnosis,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [list, q]);

  const { page, setPage, pageSize, setPageSize, paged, total, pageCount } = usePagination(filtered, 25);
  return (
    <div data-testid="opd-page">
      <PageHeader eyebrow="OPD" title="OPD Visits"
        subtitle="All recorded out-patient visits across your hospital."
        actions={
          <>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--hms-muted)]" />
              <Input
                placeholder="Search MRN, name, OPD ID, doctor…"
                value={q} onChange={(e) => setQ(e.target.value)}
                className="pl-8 w-64 rounded-sm border-[var(--hms-border-2)] bg-white"
                data-testid="opd-search"
              />
            </div>
            <Button
              onClick={() => nav("/patients?register=1")}
              data-testid="opd-register-patient"
              className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]"
            >
              <Plus size={14} weight="bold" className="mr-1.5" /> Register patient
            </Button>
          </>
        }
      />
      {list.length === 0 ? (
        <EmptyState title="No OPD visits yet" hint="Open a patient and record an OPD visit." />
      ) : filtered.length === 0 ? (
        <EmptyState title={`No matches for "${q}"`} hint="Try a different MRN, name, or OPD ID." />
      ) : (
        <div className="hms-card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--hms-paper)] text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
              <tr>
                <th className="text-left px-4 py-2.5">Patient Name</th>
                <th className="text-left px-4 py-2.5">OPD ID</th>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">MRN</th>
                <th className="text-left px-4 py-2.5">Doctor</th>
                <th className="text-left px-4 py-2.5">Diagnosis</th>
                <th className="text-right px-4 py-2.5">Fee</th>
                <th className="text-right px-4 py-2.5">Receipt</th>
                <th className="text-right px-4 py-2.5">Rx</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((v) => (
                <tr key={v.id} className="border-b border-[var(--hms-border)] hover:bg-[var(--hms-paper)]/50">
                  <td className="px-4 py-2 font-semibold">
                    <Link to={`/patients/${v.mrn}`} className="hover:text-[var(--hms-accent)]">{v.patient_name || "—"}</Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs font-semibold text-[var(--hms-accent)]">{v.opd_id || "—"}</td>
                  <td className="px-4 py-2 hms-tabular">{fmtDateTime(v.visit_date)}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link to={`/patients/${v.mrn}`} className="hover:text-[var(--hms-accent)]">{v.mrn}</Link>
                  </td>
                  <td className="px-4 py-2 font-medium">{v.doctor}</td>
                  <td className="px-4 py-2">{v.diagnosis || "—"}</td>
                  <td className="px-4 py-2 text-right hms-tabular">₹{Number(v.fee || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => printPdf(`/pdf/opd-receipt/${v.id}`).catch((e) => toast.error(e.message || "Print failed"))}
                      data-testid={`opd-print-receipt-${v.id}`}
                      className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-[var(--hms-text-2)] hover:text-[var(--hms-accent)] hover:bg-[var(--hms-hover)] cursor-pointer transition-colors"
                      title="Print OPD receipt (A5)"
                      aria-label="Print OPD receipt"
                    >
                      <Receipt size={16} weight="bold" />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => printPdf(`/pdf/opd-rx/${v.id}`).catch((e) => toast.error(e.message || "Print failed"))}
                      data-testid={`opd-print-rx-${v.id}`}
                      className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-[var(--hms-text-2)] hover:text-[var(--hms-accent)] hover:bg-[var(--hms-hover)] cursor-pointer transition-colors"
                      title="Print prescription (A5)"
                      aria-label="Print prescription"
                    >
                      <Printer size={16} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} setPage={setPage} pageSize={pageSize}
            setPageSize={setPageSize} total={total} pageCount={pageCount} testidPrefix="opd" />
        </div>
      )}
    </div>
  );
}
